import { useTranslation } from 'react-i18next';
import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import ScrollReveal from '@/components/base/ScrollReveal';

const PROCESS_STEPS = [
  { number: '01', key: 'process1', icon: 'ri-search-eye-line', items: ['process1Item1', 'process1Item2'] },
  { number: '02', key: 'process2', icon: 'ri-lightbulb-flash-line', items: ['process2Item1', 'process2Item2'] },
  { number: '03', key: 'process3', icon: 'ri-rocket-2-line', items: ['process3Item1', 'process3Item2', 'process3Item3'] },
  { number: '04', key: 'process4', icon: 'ri-bar-chart-grouped-line', items: ['process4Item1', 'process4Item2'] },
  { number: '05', key: 'process5', icon: 'ri-seedling-line', items: ['process5Item1', 'process5Item2'] },
];

function ProcessCard({ step, index, total }: { step: typeof PROCESS_STEPS[0]; index: number; total: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-50px 0px -50px 0px', amount: 0.4 });
  const { t } = useTranslation();
  const isEven = index % 2 === 0;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: isEven ? -40 : 40 }}
      animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: isEven ? -40 : 40 }}
      transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
      className={`flex items-start gap-6 ${isEven ? '' : 'flex-row-reverse'}`}
    >
      <div className={`flex-1 ${isEven ? 'text-right' : 'text-left'}`}>
        <div className={`bg-white rounded-2xl p-6 border border-gray-100 shadow-[0_4px_24px_rgba(0,0,0,0.03)] hover:shadow-[0_12px_48px_rgba(0,0,0,0.06)] transition-all duration-400 inline-block ${isEven ? 'text-left' : 'text-left'}`}>
          <span className="text-[#C8A24A] text-xs font-bold tracking-widest uppercase mb-2 block">
            {t('trafficMaster.stepLabel')} {step.number}
          </span>
          <h3 className="font-heading text-lg font-bold text-[#111111] mb-3">
            {t(`trafficMaster.${step.key}`)}
          </h3>
          <ul className="space-y-1.5">
            {step.items.map((itemKey) => (
              <li key={itemKey} className="flex items-center gap-2 text-gray-600 text-sm">
                <i className="ri-check-line text-[#E53935] text-sm flex-shrink-0"></i>
                {t(`trafficMaster.${itemKey}`)}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="flex flex-col items-center flex-shrink-0">
        <motion.div
          initial={{ scale: 0 }}
          animate={isInView ? { scale: 1 } : { scale: 0 }}
          transition={{ duration: 0.5, delay: 0.3, ease: 'easeOut' }}
          className="w-16 h-16 rounded-2xl bg-[#E53935] text-white flex items-center justify-center shadow-[0_4px_20px_rgba(229,57,53,0.3)] relative z-10"
        >
          <i className={`${step.icon} text-2xl`}></i>
        </motion.div>
        {index < total - 1 && (
          <motion.div
            initial={{ scaleY: 0 }}
            animate={isInView ? { scaleY: 1 } : { scaleY: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="w-[2px] h-16 bg-gradient-to-b from-[#E53935]/30 to-transparent"
            style={{ transformOrigin: 'top' }}
          ></motion.div>
        )}
      </div>

      <div className="flex-1 hidden lg:block"></div>
    </motion.div>
  );
}

export default function Process() {
  const { t } = useTranslation();

  return (
    <section className="py-20 md:py-32 bg-[#F7F8FA]">
      <div className="max-w-4xl mx-auto px-6 md:px-10">
        <ScrollReveal>
          <div className="text-center mb-20">
            <span className="inline-block px-4 py-1.5 rounded-full bg-white text-[#E53935] text-xs font-semibold tracking-wider uppercase mb-5 border border-gray-100">
              {t('trafficMaster.processTag')}
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-[#111111] leading-tight">
              {t('trafficMaster.processTitle')}
            </h2>
          </div>
        </ScrollReveal>

        <div className="space-y-10 lg:space-y-0">
          {PROCESS_STEPS.map((step, i) => (
            <ProcessCard key={step.key} step={step} index={i} total={PROCESS_STEPS.length} />
          ))}
        </div>
      </div>
    </section>
  );
}